/**
 * Reseller portal — API access page (v147).
 *
 * The reseller views the API keys InteGolf issued them, edits the allowed
 * website domains on their publishable key, and rotates a secret. New keys
 * are issued by InteGolf (admin side); this page is the reseller's
 * self-serve view + the link to the API documentation.
 */
import { useEffect, useState } from 'react'
import ResellerShell from './ResellerShell'
import ApiKeysManager from '../../components/ApiKeysManager'
import { useLanguage } from '../../context/LanguageContext'
import { tournamentApi } from '../../services/api'

export default function ApiAccess() {
  const { t } = useLanguage()
  return (
    <ResellerShell title={t('sp_api_access', 'API access')}>
      <p style={{ color: '#64748b', fontSize: 14, lineHeight: 1.6, maxWidth: 760 }}>
        {t('sp_api_access_intro', 'Use these keys to pull your courses, hotels and tee-time availability, create bookings, and manage them from your own website or back-end. Add them to your integration alongside your embed code. Need a key or a change? Contact InteGolf.')}
      </p>
      <div style={{ marginTop: 20, maxWidth: 820 }}>
        <ApiKeysManager mode="portal" docsHref="/developers" />
      </div>
      <div style={{ marginTop: 28, maxWidth: 820 }}>
        <ApiUsagePanel t={t} />
      </div>
    </ResellerShell>
  )
}

const fmt = (d) => { if (!d) return ''; try { return new Date(d).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) } catch { return '' } }
const dayLabel = (d) => { try { return new Date(d).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }) } catch { return '' } }
const statusColor = (s) => s >= 500 ? '#b91c1c' : s >= 400 ? '#b45309' : '#16a34a'

function ApiUsagePanel({ t }) {
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')

  useEffect(() => {
    tournamentApi.get('/portal/reseller/api-keys/usage')
      .then(r => setData(r.data)).catch(e => setErr(e?.response?.data?.message || e.message))
  }, [])

  // Show an error card instead of silently vanishing when the usage endpoint fails.
  if (err) return (
    <div style={{ background: '#fff', border: '1px solid #fecaca', borderRadius: 10, padding: 16 }}>
      <h3 style={{ margin: '0 0 6px', fontSize: 16, color: '#0f172a' }}>{t('sp_api_usage_title', 'API usage')}</h3>
      <div style={{ color: '#b91c1c', fontSize: 13 }}>
        {t('sp_api_usage_error', 'Couldn’t load API usage right now. Please try again shortly.')}
        <span style={{ color: '#94a3b8' }}> {err}</span>
      </div>
    </div>
  )
  if (!data) return <div style={{ color: '#94a3b8', fontSize: 13 }}>{t('sp_api_usage_loading', 'Loading usage…')}</div>

  const { totals = {}, trend = [], byEndpoint = [], byKey = [], recent = [], recentErrors = [], lifetime = 0 } = data
  const hasAny = lifetime > 0 || totals.last30 > 0
  const maxTrend = Math.max(1, ...trend.map(x => x.requests))

  const card = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16 }
  const th = { padding: '4px 8px', fontWeight: 600, color: '#64748b', textAlign: 'left', fontSize: 12 }
  const td = { padding: '4px 8px', color: '#334155', fontSize: 12.5, borderTop: '1px solid #eef2f7' }

  return (
    <div style={card}>
      <h3 style={{ margin: '0 0 14px', fontSize: 16, color: '#0f172a' }}>{t('sp_api_usage_title', 'API usage')}</h3>

      {!hasAny ? (
        <div style={{ color: '#94a3b8', fontSize: 13 }}>{t('sp_api_usage_none', 'No API calls recorded yet — usage will appear here as your keys make requests.')}</div>
      ) : (
        <>
          {/* Stat tiles */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: 10, marginBottom: 16 }}>
            <Stat label={t('sp_api_usage_today', 'Today')} value={totals.today || 0} />
            <Stat label={t('sp_api_usage_7d', 'Last 7 days')} value={totals.last7 || 0} />
            <Stat label={t('sp_api_usage_30d', 'Last 30 days')} value={totals.last30 || 0} />
            <Stat label={t('sp_api_usage_lifetime', 'All time')} value={lifetime} />
            <Stat label={t('sp_api_usage_errors', 'Errors (30d)')} value={totals.errors30 || 0} color={totals.errors30 > 0 ? '#b45309' : '#0f172a'} />
          </div>

          {/* Trend */}
          {trend.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{t('sp_api_usage_trend', 'Requests — last 14 days')}</div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 70 }}>
                {trend.map(x => (
                  <div key={x.date} title={`${dayLabel(x.date)}: ${x.requests} req${x.errors ? ` · ${x.errors} err` : ''}`}
                    style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                    <div style={{ width: '100%', background: '#e0f2fe', borderRadius: '3px 3px 0 0', height: `${Math.round((x.requests / maxTrend) * 56) + 2}px`, position: 'relative' }}>
                      {x.errors > 0 && <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: '#fecaca', height: `${Math.round((x.errors / maxTrend) * 56)}px` }} />}
                    </div>
                    <div style={{ fontSize: 9, color: '#94a3b8' }}>{dayLabel(x.date).replace(/^[A-Za-z]+ /, '')}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top endpoints */}
          {byEndpoint.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{t('sp_api_usage_endpoints', 'Top endpoints (30 days)')}</div>
              <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #eef2f7', borderRadius: 6 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={th}>{t('sp_api_usage_endpoint', 'Endpoint')}</th><th style={{ ...th, textAlign: 'right' }}>{t('sp_api_usage_requests', 'Requests')}</th><th style={{ ...th, textAlign: 'right' }}>{t('sp_api_usage_errors_short', 'Errors')}</th></tr></thead>
                  <tbody>{byEndpoint.map((e, i) => (
                    <tr key={i}>
                      <td style={{ ...td, fontFamily: 'monospace', fontSize: 11.5 }}>{e.endpoint}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{e.requests}</td>
                      <td style={{ ...td, textAlign: 'right', color: e.errors > 0 ? '#b45309' : '#94a3b8' }}>{e.errors || 0}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}

          {/* Recent errors — WHAT failed (status + error code + message), not
              just a count. Persisted, so it survives restarts. */}
          {recentErrors.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{t('sp_api_usage_recent_errors', 'Recent errors (30 days)')}</div>
              <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #fee2e2', borderRadius: 6 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={th}>{t('sp_api_usage_when', 'When')}</th>
                    <th style={th}>{t('sp_api_usage_endpoint', 'Endpoint')}</th>
                    <th style={{ ...th, textAlign: 'right' }}>{t('sp_api_usage_status', 'Status')}</th>
                    <th style={th}>{t('sp_api_usage_detail', 'Detail')}</th>
                  </tr></thead>
                  <tbody>{recentErrors.map((e, i) => (
                    <tr key={i}>
                      <td style={{ ...td, whiteSpace: 'nowrap', color: '#64748b' }}>{fmt(e.at)}</td>
                      <td style={{ ...td, fontFamily: 'monospace', fontSize: 11.5 }}>{e.method} {e.endpoint}</td>
                      <td style={{ ...td, textAlign: 'right', color: statusColor(e.status), fontWeight: 600 }}>{e.status}</td>
                      <td style={{ ...td, fontSize: 11.5 }}>
                        {e.errorCode && <code style={{ color: '#b91c1c' }}>{e.errorCode}</code>}
                        {e.message ? <span style={{ color: '#475569' }}> {e.message}</span> : null}
                        {e.keyPrefix ? <span style={{ color: '#cbd5e1' }}> · {e.keyPrefix}</span> : null}
                      </td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}

          {/* By key */}
          {byKey.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{t('sp_api_usage_by_key', 'By key (30 days)')}</div>
              {byKey.map(k => (
                <div key={k.apiKeyId} style={{ fontSize: 12.5, color: '#334155', padding: '3px 0' }}>
                  <span style={{ fontFamily: 'monospace' }}>{k.keyPrefix || `#${k.apiKeyId}`}</span>
                  <span style={{ color: '#94a3b8' }}> · {k.keyType}{k.label ? ` · ${k.label}` : ''}</span>
                  <strong style={{ marginLeft: 6 }}>{k.requests30}</strong> <span style={{ color: '#94a3b8' }}>{t('sp_api_usage_requests', 'requests')}</span>
                </div>
              ))}
            </div>
          )}

          {/* Recent */}
          {recent.length > 0 && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{t('sp_api_usage_recent', 'Recent calls')}</div>
              <div style={{ maxHeight: 160, overflowY: 'auto', fontSize: 12, fontFamily: 'monospace', color: '#475569' }}>
                {recent.map((r, i) => (
                  <div key={i} style={{ padding: '1px 0' }}>
                    <span style={{ color: '#94a3b8' }}>{fmt(r.at).split(', ')[1] || ''}</span>{' '}
                    <span style={{ color: statusColor(r.status), fontWeight: 600 }}>{r.status}</span>{' '}
                    {r.endpoint}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function Stat({ label, value, color }) {
  return (
    <div style={{ padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
      <div style={{ fontSize: 11.5, color: '#64748b' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || '#0f172a' }}>{Number(value).toLocaleString()}</div>
    </div>
  )
}
